import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Target, Plus, Trash2, Lock, Users, Eye, X, Calendar } from "lucide-react";
import HouseholdNav from "@/components/HouseholdNav";

const KIND_META = {
    "Personal": { icon: Lock,  tone: "text-stone-600",  bg: "bg-stone-100" },
    "Couple":   { icon: Users, tone: "text-amber-700",  bg: "bg-amber-50" },
    "Family":   { icon: Users, tone: "text-emerald-700",bg: "bg-emerald-50" },
};

const VIS_ICON = { "Private": Lock, "Selected Members": Users, "Shared": Eye };

function fmtINR(v) {
    const n = Number(v || 0);
    if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
    if (n >= 100000)  return `₹${(n / 100000).toFixed(2)} L`;
    if (n >= 1000)    return `₹${(n / 1000).toFixed(1)}K`;
    return `₹${n.toFixed(0)}`;
}

function GoalCard({ g, members, onDelete }) {
    const meta = KIND_META[g.kind] || KIND_META.Personal;
    const Icon = meta.icon;
    const VIcon = VIS_ICON[g.visibility] || Users;
    const contribs = (g.contributors || []).map(uid => members.find(m => m.user_id === uid)?.name || "Member");
    return (
        <div className="rounded-2xl border border-stone-200 bg-white p-4" data-testid={`goal-${g.id}`}>
            <div className="flex items-start gap-2 mb-1.5">
                <span className={`inline-flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-widest ${meta.tone} ${meta.bg} rounded-full px-2 py-0.5`}>
                    <Icon size={10} /> {g.kind}
                </span>
                <span className="text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold flex items-center gap-1">
                    <VIcon size={10} /> {g.visibility}
                </span>
                <button onClick={() => onDelete(g.id)} title="Delete"
                        className="ml-auto text-stone-400 hover:text-rose-700" data-testid={`goal-del-${g.id}`}>
                    <Trash2 size={12} />
                </button>
            </div>
            <div className="text-[16px] font-semibold text-stone-900">{g.title}</div>
            <div className="text-[11.5px] text-stone-500 mt-0.5">
                {g.goal_type || "Custom"} · {g.target_amount ? `Target ${fmtINR(g.target_amount)}` : "No target amount"}
                {g.target_date && <> · <Calendar size={9} className="inline mx-1" />{g.target_date}</>}
            </div>
            {contribs.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                    {contribs.map((n, i) => (
                        <span key={i} className="text-[10.5px] font-semibold px-2 py-0.5 rounded-full bg-stone-100 text-stone-700">
                            {n}
                        </span>
                    ))}
                </div>
            )}
            {g.note && <div className="text-[11.5px] text-stone-500 mt-2 italic">{g.note}</div>}
        </div>
    );
}

export default function HouseholdGoals() {
    const [rows, setRows] = useState([]);
    const [meta, setMeta] = useState({});
    const [members, setMembers] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        title: "", kind: "Personal", goal_type: "Custom",
        target_amount: "", target_date: "",
        contributors: [], visibility: "", note: "",
    });

    const load = async () => {
        const [g, h] = await Promise.all([
            api.get("/household/goals").catch(() => null),
            api.get("/household").catch(() => null),
        ]);
        setRows(g?.data?.goals || []);
        setMeta(g?.data?.meta || {});
        setMembers((h?.data?.members || []).filter(m => m.status === "Active"));
    };
    useEffect(() => { load(); }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.title) return toast.error("Give the goal a name");
        try {
            const payload = { ...form,
                target_amount: form.target_amount === "" ? null : Number(form.target_amount) };
            if (!payload.visibility) delete payload.visibility;
            await api.post("/household/goals", payload);
            toast.success("Goal saved");
            setOpen(false);
            setForm({ title: "", kind: "Personal", goal_type: "Custom",
                      target_amount: "", target_date: "", contributors: [],
                      visibility: "", note: "" });
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't save");
        }
    };

    const del = async (id) => {
        if (!window.confirm("Delete this goal?")) return;
        try { await api.delete(`/household/goals/${id}`); toast.success("Deleted"); load(); }
        catch (err) { toast.error(err?.response?.data?.detail || "Couldn't delete"); }
    };

    const toggleContrib = (uid) => setForm(f => ({ ...f,
        contributors: f.contributors.includes(uid)
            ? f.contributors.filter(x => x !== uid) : [...f.contributors, uid] }));

    return (
        <div className="max-w-6xl mx-auto p-6 md:p-8" data-testid="household-goals-page">
            <HouseholdNav />
            <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
                <div>
                    <div className="text-[11px] uppercase tracking-widest text-amber-800 font-semibold flex items-center gap-1.5">
                        <Target size={12} /> VAULT · Together · Goals
                    </div>
                    <h1 className="font-display text-3xl sm:text-4xl text-stone-900 leading-none mt-2">
                        The things you&rsquo;re organising towards.
                    </h1>
                    <p className="text-sm text-stone-500 mt-3 max-w-2xl">
                        Personal, couple or family goals. VAULT never suggests amounts or timelines — you set
                        them. This is organisational attribution only, we never move money.
                    </p>
                </div>
                <button onClick={() => setOpen(true)}
                        className="inline-flex items-center gap-1 px-3.5 py-2 rounded-full bg-stone-900 text-white text-[13px] font-semibold hover:bg-stone-700"
                        data-testid="goal-add">
                    <Plus size={12} /> New goal
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {rows.map(g => <GoalCard key={g.id} g={g} members={members} onDelete={del} />)}
                {rows.length === 0 && (
                    <div className="col-span-full text-center py-16 text-stone-500 text-[13px]" data-testid="goal-empty">
                        No goals yet. Add one to start organising towards a shared or personal target.
                    </div>
                )}
            </div>

            {open && (
                <div className="fixed inset-0 z-50 bg-stone-900/60 flex items-center justify-center p-4"
                     onClick={() => setOpen(false)} data-testid="goal-form-modal">
                    <form onSubmit={submit} onClick={e => e.stopPropagation()}
                          className="bg-white rounded-2xl max-w-lg w-full p-5 space-y-3">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-display text-stone-900">New goal</h3>
                            <button type="button" onClick={() => setOpen(false)} className="text-stone-400 hover:text-stone-700"><X size={14} /></button>
                        </div>
                        <label className="block">
                            <span className="text-[10.5px] uppercase tracking-widest text-stone-500">Title</span>
                            <input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))}
                                   className="mt-1 w-full border border-stone-300 rounded px-2 py-1.5 text-[13px]"
                                   data-testid="goal-title" required />
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                            <label className="block">
                                <span className="text-[10.5px] uppercase tracking-widest text-stone-500">Kind</span>
                                <select value={form.kind} onChange={e => setForm(f => ({...f, kind: e.target.value}))}
                                        className="mt-1 w-full border border-stone-300 rounded px-2 py-1.5 text-[13px] bg-white"
                                        data-testid="goal-kind">
                                    {(meta.kinds || ["Personal", "Couple", "Family"]).map(k => <option key={k}>{k}</option>)}
                                </select>
                            </label>
                            <label className="block">
                                <span className="text-[10.5px] uppercase tracking-widest text-stone-500">Type</span>
                                <select value={form.goal_type} onChange={e => setForm(f => ({...f, goal_type: e.target.value}))}
                                        className="mt-1 w-full border border-stone-300 rounded px-2 py-1.5 text-[13px] bg-white"
                                        data-testid="goal-type">
                                    {(meta.types || ["Custom"]).map(t => <option key={t}>{t}</option>)}
                                </select>
                            </label>
                            <label className="block">
                                <span className="text-[10.5px] uppercase tracking-widest text-stone-500">Target amount</span>
                                <input type="number" value={form.target_amount}
                                       onChange={e => setForm(f => ({...f, target_amount: e.target.value}))}
                                       placeholder="Optional"
                                       className="mt-1 w-full border border-stone-300 rounded px-2 py-1.5 text-[13px]"
                                       data-testid="goal-target-amount" />
                            </label>
                            <label className="block">
                                <span className="text-[10.5px] uppercase tracking-widest text-stone-500">Target date</span>
                                <input type="date" value={form.target_date}
                                       onChange={e => setForm(f => ({...f, target_date: e.target.value}))}
                                       className="mt-1 w-full border border-stone-300 rounded px-2 py-1.5 text-[13px]"
                                       data-testid="goal-target-date" />
                            </label>
                        </div>
                        <label className="block">
                            <span className="text-[10.5px] uppercase tracking-widest text-stone-500">Visibility</span>
                            <select value={form.visibility} onChange={e => setForm(f => ({...f, visibility: e.target.value}))}
                                    className="mt-1 w-full border border-stone-300 rounded px-2 py-1.5 text-[13px] bg-white"
                                    data-testid="goal-visibility">
                                <option value="">Default (Private for Personal · Selected for Couple/Family)</option>
                                {(meta.visibilities || ["Private", "Selected Members", "Shared"]).map(v => <option key={v}>{v}</option>)}
                            </select>
                        </label>
                        <div>
                            <span className="text-[10.5px] uppercase tracking-widest text-stone-500">Contributors</span>
                            <div className="mt-1 flex flex-wrap gap-1.5">
                                {members.map(m => {
                                    const on = form.contributors.includes(m.user_id);
                                    return (
                                        <button key={m.id} type="button"
                                                onClick={() => toggleContrib(m.user_id)}
                                                className={`text-[11.5px] font-semibold px-2.5 py-1 rounded-full border ${on ? "bg-stone-900 text-white border-stone-900" : "border-stone-300 text-stone-700"}`}
                                                data-testid={`goal-contrib-${m.user_id}`}>
                                            {m.name || m.email}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                        <label className="block">
                            <span className="text-[10.5px] uppercase tracking-widest text-stone-500">Note</span>
                            <textarea value={form.note} onChange={e => setForm(f => ({...f, note: e.target.value}))}
                                      rows={2}
                                      className="mt-1 w-full border border-stone-300 rounded px-2 py-1.5 text-[13px]"
                                      data-testid="goal-note" />
                        </label>
                        <div className="flex justify-end gap-2 pt-2">
                            <button type="button" onClick={() => setOpen(false)}
                                    className="text-[12px] font-semibold text-stone-700 border border-stone-300 rounded-full px-3.5 py-1.5">
                                Cancel
                            </button>
                            <button type="submit"
                                    className="text-[12px] font-semibold text-white bg-stone-900 hover:bg-stone-700 rounded-full px-3.5 py-1.5"
                                    data-testid="goal-save">
                                Save goal
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}
